import classNames from 'classnames';

import { REWARD_POOL_STATUS, RewardPool } from '@shared/api/types/rewardPools';

import { useSelfEntriesAmountByCurrency } from '@store:web/components/entities/rewardPool';
import { Badge, BadgeSize, BadgeVariant } from '@ui/uikit/components/common/Badge';

import { useRequirements } from '../../model/useRequirements';
import { GetTicketsButton } from './buttons/GetTicketsButton/GetTicketsButton';
import { ViewTicketsButton } from './buttons/ViewTicketsButton/ViewTicketsButton';
import { List } from './List/List';
import { ListItem } from './List/ListItem';
import { ListWrapper } from './List/ListWrapper';

type Props = {
  rewardPool: RewardPool;
  hasBorders?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
};

export const EntryOptionsList = ({
  rewardPool,
  showFooter = true,
  showHeader = true,
  hasBorders = true,
}: Props) => {
  const { requirements, hasRequirements, query } = useRequirements(rewardPool.id);
  const isPoolCompleted = rewardPool.status === REWARD_POOL_STATUS.COMPLETED;
  const { entriesAmountByCurrency } = useSelfEntriesAmountByCurrency({ poolId: rewardPool.id });

  return (
    <ListWrapper
      showFooter={showFooter}
      header={showHeader && <h2 className="heading-h2-adaptive">Raffle Entry Options</h2>}
    >
      <List
        isEmpty={!hasRequirements}
        query={query}
        hasBorders={hasBorders}
        data-qa="requirements_list"
      >
        {requirements.map(({ id, currencyType, title, description, action }, index) => {
          const entryAmount = currencyType?.currency
            ? entriesAmountByCurrency[currencyType.currency]
            : 0;
          return (
            <ListItem
              isLastItem={index === requirements.length - 1}
              key={id}
              data-qa={`requirement-id_${id}`}
              isDisabled={isPoolCompleted}
              label={title}
              description={description}
              buttons={
                <>
                  <ViewTicketsButton
                    rewardPool={rewardPool}
                    currency={currencyType?.currency}
                    className={classNames({ invisible: !entryAmount })}
                    dataQaAttribute="view-my-tickets-button"
                  >
                    View Tickets
                  </ViewTicketsButton>
                  {!isPoolCompleted && currencyType && (
                    <GetTicketsButton
                      rewardPool={rewardPool}
                      currencyType={currencyType}
                      dataQaAttribute={`get-ticket-for-${currencyType.currency}`}
                    >
                      {entryAmount > 0 ? 'Get More' : action?.label}
                    </GetTicketsButton>
                  )}
                </>
              }
              counter={
                <Badge
                  variant={entryAmount > 0 ? BadgeVariant.GREEN : BadgeVariant.DEFAULT}
                  text={entryAmount > 0 ? String(entryAmount) : '+1'}
                  size={BadgeSize.LARGE}
                  className="w-12"
                />
              }
            />
          );
        })}
      </List>
    </ListWrapper>
  );
};
